import React, { useState, useEffect, useRef, useContext, createContext } from "react";

// ── Language types ────────────────────────────────────────────────────────────
type Lang = "it" | "en" | "fr";

// ── Translations ──────────────────────────────────────────────────────────────
const T = {
  it: {
    assoc: "Associazione degli Studenti Camerunensi di Torino · AECT",
    tagline: "Oltre i confini: Cultura, Sport e Visione",
    subtitle:
      "54ª Edizione della Festa dell'Unità Nazionale del Camerun. Un mese di sport, cultura e visione a Torino.",
    btnBook: "Prenota Ora",
    scroll: "SCROLL",
    pillarsLabel: "PROGRAMMA",
    pillarsTitle: "Un Programma che Unisce",
    pillars: [
      { icon: "🎤", title: "Cultura", text: "Spettacoli, eloquenza, cinema e arte per celebrare le radici, il futuro e l'innovazione." },
      { icon: "⚽", title: "Sport", text: "Tornei e sfide che uniscono passione, energia e spirito di squadra." },
      { icon: "🌟", title: "Visione", text: "Dialogo, ispirazione, premiazioni e networking per costruire una comunità senza confine e creare delle opportunità." },
    ],
    calLabel: "MAGGIO 2026",
    calTitle: "Calendario degli eventi",
    calSub: "Scrivi la storia con noi. Ogni data è un capitolo.",
    regLabel: "REGISTRAZIONE UFFICIALE",
    regTitle: "Pronto a scrivere la storia?",
    regDesc: "I posti per gli eventi di maggio di Beyond Borders Project 2026 sono disponibili.",
    regDesc2: "Clicca sul pulsante qui sotto per accedere al modulo ufficiale, scegliere i tuoi eventi e confermare la tua presenza.",
    regBtn: "Vai al Modulo",
    footerBtn: "REGISTRATI ORA",
    footerQuote: '"Salva questa pagina e segui il viaggio di Beyond Borders."',
    partnersLabel: "I NOSTRI PARTNER",
    facebook: "Facebook", instagram: "Instagram", website: "Sito Web",
    moreBtn: "Dettagli", lessBtn: "Chiudi",
  },
  en: {
    assoc: "Association of Cameroonian Students in Turin · AECT",
    tagline: "Beyond Borders: Culture, Sport & Vision",
    subtitle:
      "54th Edition of the Cameroon National Unity Festival. A month of sport, culture and vision in Turin.",
    btnBook: "Book Now",
    scroll: "SCROLL",
    pillarsLabel: "PROGRAMME",
    pillarsTitle: "A Programme That Unites",
    pillars: [
      { icon: "🎤", title: "Culture", text: "Shows, eloquence, cinema and art to celebrate roots, the future and innovation." },
      { icon: "⚽", title: "Sport", text: "Tournaments and challenges that unite passion, energy and team spirit." },
      { icon: "🌟", title: "Vision", text: "Dialogue, inspiration, awards and networking to build a borderless community and create opportunities." },
    ],
    calLabel: "MAY 2026",
    calTitle: "Event Calendar",
    calSub: "Write history with us. Every date is a chapter.",
    regLabel: "OFFICIAL REGISTRATION",
    regTitle: "Ready to write history?",
    regDesc: "Spots for the May events of Beyond Borders Project 2026 are available.",
    regDesc2: "Click the button below to access the official form, choose your events and confirm your attendance.",
    regBtn: "Go to Form",
    footerBtn: "REGISTER NOW",
    footerQuote: '"Bookmark this page and follow the Beyond Borders journey."',
    partnersLabel: "OUR PARTNERS",
    facebook: "Facebook", instagram: "Instagram", website: "Website",
    moreBtn: "Details", lessBtn: "Close",
  },
  fr: {
    assoc: "Association des Étudiants Camerounais de Turin · AECT",
    tagline: "Au-delà des frontières : Culture, Sport et Vision",
    subtitle:
      "54e Édition de la Fête de l'Unité Nationale du Cameroun. Un mois de sport, culture et vision à Turin.",
    btnBook: "Réserver Maintenant",
    scroll: "DÉFILER",
    pillarsLabel: "PROGRAMME",
    pillarsTitle: "Un Programme qui Unit",
    pillars: [
      { icon: "🎤", title: "Culture", text: "Spectacles, éloquence, cinéma et art pour célébrer les racines, l'avenir et l'innovation." },
      { icon: "⚽", title: "Sport", text: "Tournois et défis qui unissent passion, énergie et esprit d'équipe." },
      { icon: "🌟", title: "Vision", text: "Dialogue, inspiration, remises de prix et networking pour bâtir une communauté sans frontières et créer des opportunités." },
    ],
    calLabel: "MAI 2026",
    calTitle: "Calendrier des événements",
    calSub: "Écrivez l'histoire avec nous. Chaque date est un chapitre.",
    regLabel: "INSCRIPTION OFFICIELLE",
    regTitle: "Prêt à écrire l'histoire ?",
    regDesc: "Les places pour les événements de mai de Beyond Borders Project 2026 sont disponibles.",
    regDesc2: "Cliquez sur le bouton ci-dessous pour accéder au formulaire officiel, choisir vos événements et confirmer votre présence.",
    regBtn: "Accéder au Formulaire",
    footerBtn: "S'INSCRIRE",
    footerQuote: '"Sauvegardez cette page et suivez le voyage de Beyond Borders."',
    partnersLabel: "NOS PARTENAIRES",
    facebook: "Facebook", instagram: "Instagram", website: "Site Web",
    moreBtn: "Détails", lessBtn: "Fermer",
  },
};

// ── Event type ────────────────────────────────────────────────────────────────
interface EventItem {
  id: number; date: string; icon: string; tag: string; tagColor: string;
  title: string; subtitle: string; hook: string; desc: string;
  details: string[];
  formUrl?: string; driveUrl?: string; formCta?: string; driveCta?: string;
}

// ── Events per language ───────────────────────────────────────────────────────
const EVENTS_MULTI: Record<Lang, EventItem[]> = {
  it: [
    { id: 1, date: "04 Mag – 17 Mag", icon: "🎤", tag: "ONLINE", tagColor: "#10b981",
      title: "Voice of Impact", subtitle: "Fasi Online",
      hook: "La tua parola ha un peso. Ispira il cambiamento.",
      desc: "Concorso di Eloquenza online. Esprimi la tua visione, conquista il palcoscenico della finale in presenza.",
      details: ["Aperto a tutti gli studenti universitari", "Fase online: 4–17 Maggio · Finale in presenza", "Premi per i migliori oratori", "Iscriviti tramite il modulo ufficiale AECT"],
      formUrl: "https://forms.gle/TFWBhrpN1BEivxxNA", driveUrl: "https://drive.google.com/drive/folders/1ZdhsC9up-ek00PCXM-EFZv0EQYzJJETs?usp=sharing",
      formCta: "🎤 Prendi il microfono", driveCta: "📁 Regolamento & Informazioni" },
    { id: 2, date: "9 · 17 · 23 Mag", icon: "⚽", tag: "LIVE", tagColor: "#ef4444",
      title: "Torneo dell'Unità", subtitle: "Sport",
      hook: "La gloria si scrive sul campo e sulla pista di corsa.",
      desc: "Calcio a 8 Maschile, Calcio a 5 Femminile, Basketball Misto, Beach volley Misto, Atletica leggera. Tre giornate di competizione, spirito di squadra e passione pura.",
      details: ["300+ giovani · 13+ nazionalità rappresentate", "5 discipline: Calcio 8 M · Calcio 5 F · Basket · Beach Volley · Atletica", "Luogo: CUS Torino, Via Panetti 30 — 9, 17 e 23 Maggio", "Iscrizioni GRATUITE entro il 6 Maggio · bureauaect@gmail.com · +39 351 982 5779"],
      formUrl: "https://forms.gle/A9exVfeqyeD2oy5z8", driveUrl: "https://drive.google.com/drive/folders/1B79VGqIdYrbo6vu9DtrbCtor5fCKYciJ?usp=sharing",
      formCta: "⚽ Iscriviti al Torneo", driveCta: "📁 Regolamento & Informazioni" },
    { id: 3, date: "15 – 16 Mag", icon: "🏆", tag: "LIVE", tagColor: "#ef4444",
      title: "Attività CUS Torino", subtitle: "Sport Universitario",
      hook: "Vinciamo insieme con lo sport universitario.",
      desc: "Due giornate in collaborazione con il Centro Universitario Sportivo di Torino, con l'International University Challenge (IUC).",
      details: ["Partenariato ufficiale con il CUS Torino", "International University Challenge (IUC) — competizione studentesca universitaria", "15–16 Maggio 2026 · Aperto a studenti di tutti gli atenei piemontesi"] },
    { id: 4, date: "21 Maggio", icon: "🎬", tag: "LIVE", tagColor: "#ef4444",
      title: "Serata Cinema", subtitle: "NGANù",
      hook: "Emozioni in proiezione.",
      desc: "Proiezione esclusiva del film camerunese NGANù di Kang Quintus. Dibattito con il produttore.",
      details: ["Film camerunese NGANù di Kang Quintus — première esclusiva a Torino", "Dibattito aperto con il produttore", "21 Maggio 2026 · Ingresso su iscrizione"],
      formUrl: "https://forms.gle/xE8wvhWZzf1oesPQ6", driveUrl: "https://drive.google.com/drive/folders/1I69UQcgQkesxbe5TRUDolERXgpaVw2Cg?usp=sharing",
      formCta: "🎬 Prenota il tuo posto", driveCta: "📁 Regolamento & Informazioni" },
    { id: 5, date: "22 Maggio", icon: "🗣️", tag: "LIVE", tagColor: "#ef4444",
      title: "Conferenza dell'Unità", subtitle: "Unità & Talento",
      hook: "Dove le idee prendono vita e il talento viene incoronato.",
      desc: "Conferenza dell'Unità sui temi: diaspora, cooperazione finanziata, traduzione.",
      details: ["Temi: diaspora, cooperazione finanziata, traduzione", "Interventi di esperti, ricercatori e rappresentanti della comunità", "22 Maggio 2026 · Ingresso libero"],
      formUrl: "https://forms.gle/o125r2Fv6MdYJV8s9", driveUrl: "https://drive.google.com/drive/folders/1wX_igwL9jm8pJyjRDjc_ObdIs8uTWTcw?usp=sharing",
      formCta: "🗣️ Registrati alla Conferenza", driveCta: "📁 Regolamento & Informazioni" },
    { id: 6, date: "23 Maggio", icon: "🌟", tag: "GALA", tagColor: "#d4a017",
      title: "Unity Day — Grande Serata Culturale", subtitle: "Serata Culturale",
      hook: "L'evento dell'anno: cultura e celebrazione.",
      desc: "Best Poem Challenge, Concorso di cucina, Sfilate, Canto & Ballo, Festi Bami e Premiazioni.",
      details: ["Best Poem Challenge · Concorso di cucina camerunese", "Sfilate tradizionali · Canto & Ballo", "Festi Bami 2026 · Premiazioni ufficiali", "23 Maggio — L'appuntamento dell'anno!"],
      formUrl: "https://forms.gle/BembSfozwPiBcsXq5", driveUrl: "https://drive.google.com/drive/folders/1a_UUVx6YKUkNiW_zaBSLov8yeRXLvRHy?usp=sharing",
      formCta: "🌟 Registrati all'Unity Day", driveCta: "📁 Regolamento & Informazioni" },
  ],
  en: [
    { id: 1, date: "04 May – 17 May", icon: "🎤", tag: "ONLINE", tagColor: "#10b981",
      title: "Voice of Impact", subtitle: "Online Phases",
      hook: "Your words carry weight. Inspire change.",
      desc: "Online Eloquence Contest. Express your vision and earn your spot on the final stage.",
      details: ["Open to all university students", "Online phase: 4–17 May · In-person final: date to be confirmed", "Awards for the best speakers", "Register via the official AECT form"],
      formUrl: "https://forms.gle/TFWBhrpN1BEivxxNA", driveUrl: "https://drive.google.com/drive/folders/1ZdhsC9up-ek00PCXM-EFZv0EQYzJJETs?usp=sharing",
      formCta: "🎤 Take the mic", driveCta: "📁 Rules & Information" },
    { id: 2, date: "9 · 17 · 23 May", icon: "⚽", tag: "LIVE", tagColor: "#ef4444",
      title: "Unity Tournament", subtitle: "Sport",
      hook: "Glory is written on the field and on the track.",
      desc: "Men's 8-a-side Football, Women's Futsal, Mixed Basketball, Mixed Beach Volleyball, Athletics. Three days of competition, team spirit and pure passion.",
      details: ["300+ attendees · 13+ nationalities represented", "5 disciplines: Football · Futsal · Basketball · Beach Volleyball · Athletics", "Venue: CUS Torino, Via Panetti 30 — May 9, 17 & 23", "FREE entry — deadline 6 May · bureauaect@gmail.com · +39 351 982 5779"],
      formUrl: "https://forms.gle/A9exVfeqyeD2oy5z8", driveUrl: "https://drive.google.com/drive/folders/1B79VGqIdYrbo6vu9DtrbCtor5fCKYciJ?usp=sharing",
      formCta: "⚽ Join the Tournament", driveCta: "📁 Rules & Information" },
    { id: 3, date: "15 – 16 May", icon: "🏆", tag: "LIVE", tagColor: "#ef4444",
      title: "CUS Turin Activities", subtitle: "University Sport",
      hook: "We win together with university sport.",
      desc: "Two days in collaboration with the Turin University Sports Centre, including the International University Challenge (IUC).",
      details: ["Official partnership with CUS Torino", "International University Challenge (IUC) — university sports competition", "15–16 May 2026 · Open to students from all Piedmont universities"] },
    { id: 4, date: "21 May", icon: "🎬", tag: "LIVE", tagColor: "#ef4444",
      title: "Cinema Evening", subtitle: "NGANù",
      hook: "Emotions on screen.",
      desc: "Exclusive screening of the Cameroonian film NGANù by Kang Quintus. Debate with the producer.",
      details: ["Cameroonian film NGANù by Kang Quintus — exclusive Turin première", "Open debate with the producer", "21 May 2026 · Entry on registration"],
      formUrl: "https://forms.gle/xE8wvhWZzf1oesPQ6", driveUrl: "https://drive.google.com/drive/folders/1I69UQcgQkesxbe5TRUDolERXgpaVw2Cg?usp=sharing",
      formCta: "🎬 Book your seat", driveCta: "📁 Rules & Information" },
    { id: 5, date: "22 May", icon: "🗣️", tag: "LIVE", tagColor: "#ef4444",
      title: "Unity Conference", subtitle: "Unity & Talent",
      hook: "Where ideas come to life and talent is crowned.",
      desc: "Unity Conference on topics: diaspora, funded cooperation, translation.",
      details: ["Topics: diaspora, funded cooperation, translation", "Speeches by experts, researchers and community representatives", "22 May 2026 · Free admission"],
      formUrl: "https://forms.gle/o125r2Fv6MdYJV8s9", driveUrl: "https://drive.google.com/drive/folders/1wX_igwL9jm8pJyjRDjc_ObdIs8uTWTcw?usp=sharing",
      formCta: "🗣️ Register for the Conference", driveCta: "📁 Rules & Information" },
    { id: 6, date: "23 May", icon: "🌟", tag: "GALA", tagColor: "#d4a017",
      title: "Unity Day — Grand Cultural Evening", subtitle: "Cultural Evening",
      hook: "The event of the year: culture and celebration.",
      desc: "Best Poem Challenge, Cooking Contest, Fashion Show, Singing & Dance, Festi Bami and Awards.",
      details: ["Best Poem Challenge · Cameroonian cooking contest", "Traditional fashion shows · Singing & Dance", "Festi Bami 2026 · Official awards ceremony", "23 May — The event of the year!"],
      formUrl: "https://forms.gle/BembSfozwPiBcsXq5", driveUrl: "https://drive.google.com/drive/folders/1a_UUVx6YKUkNiW_zaBSLov8yeRXLvRHy?usp=sharing",
      formCta: "🌟 Register for Unity Day", driveCta: "📁 Rules & Information" },
  ],
  fr: [
    { id: 1, date: "04 Mai – 17 Mai", icon: "🎤", tag: "EN LIGNE", tagColor: "#10b981",
      title: "Voice of Impact", subtitle: "Phases en ligne",
      hook: "Ta parole a du poids. Inspire le changement.",
      desc: "Concours d'Éloquence en ligne. Exprime ta vision, gagne ta place sur la scène de la finale en présentiel.",
      details: ["Ouvert à tous les étudiants universitaires", "Phase en ligne : 4–17 Mai · Finale en présentiel : date à confirmer", "Prix pour les meilleurs orateurs", "Inscription via le formulaire officiel AECT"],
      formUrl: "https://forms.gle/TFWBhrpN1BEivxxNA", driveUrl: "https://drive.google.com/drive/folders/1ZdhsC9up-ek00PCXM-EFZv0EQYzJJETs?usp=sharing",
      formCta: "🎤 Prends le micro", driveCta: "📁 Règlement & Informations" },
    { id: 2, date: "9 · 17 · 23 Mai", icon: "⚽", tag: "LIVE", tagColor: "#ef4444",
      title: "Tournoi de l'Unité", subtitle: "Sport",
      hook: "La gloire s'écrit sur le terrain et sur la piste.",
      desc: "Football à 8 Masculin, Futsal Féminin, Basketball Mixte, Beach-volley Mixte, Athlétisme. Trois journées de compétition, esprit d'équipe et passion pure.",
      details: ["300+ participants · 13+ nationalités représentées", "5 disciplines : Football · Futsal · Basket · Beach-volley · Athlétisme", "Lieu : CUS Torino, Via Panetti 30 — 9, 17 et 23 Mai", "Participation GRATUITE — clôture : 6 Mai · bureauaect@gmail.com · +39 351 982 5779"],
      formUrl: "https://forms.gle/A9exVfeqyeD2oy5z8", driveUrl: "https://drive.google.com/drive/folders/1B79VGqIdYrbo6vu9DtrbCtor5fCKYciJ?usp=sharing",
      formCta: "⚽ Rejoins le Tournoi", driveCta: "📁 Règlement & Informations" },
    { id: 3, date: "15 – 16 Mai", icon: "🏆", tag: "LIVE", tagColor: "#ef4444",
      title: "Activités CUS Turin", subtitle: "Sport Universitaire",
      hook: "Gagnons ensemble grâce au sport universitaire.",
      desc: "Deux journées en collaboration avec le Centre Universitaire Sportif de Turin, avec l'International University Challenge (IUC).",
      details: ["Partenariat officiel avec le CUS Torino", "International University Challenge (IUC) — compétition sportive universitaire", "15–16 Mai 2026 · Ouvert aux étudiants des universités du Piémont"] },
    { id: 4, date: "21 Mai", icon: "🎬", tag: "LIVE", tagColor: "#ef4444",
      title: "Soirée Cinéma", subtitle: "NGANù",
      hook: "Des émotions en projection.",
      desc: "Projection exclusive du film camerounais NGANù de Kang Quintus. Débat avec le producteur.",
      details: ["Film camerounais NGANù de Kang Quintus — première exclusive à Turin", "Débat ouvert avec le producteur", "21 Mai 2026 · Entrée sur inscription"],
      formUrl: "https://forms.gle/xE8wvhWZzf1oesPQ6", driveUrl: "https://drive.google.com/drive/folders/1I69UQcgQkesxbe5TRUDolERXgpaVw2Cg?usp=sharing",
      formCta: "🎬 Réserve ta place", driveCta: "📁 Règlement & Informations" },
    { id: 5, date: "22 Mai", icon: "🗣️", tag: "LIVE", tagColor: "#ef4444",
      title: "Conférence de l'Unité", subtitle: "Unité & Talent",
      hook: "Là où les idées prennent vie et le talent est couronné.",
      desc: "Conférence de l'Unité sur les thèmes : diaspora, coopération financée, traduction.",
      details: ["Thèmes : diaspora, coopération financée, traduction", "Interventions d'experts, chercheurs et représentants de la communauté", "22 Mai 2026 · Entrée libre"],
      formUrl: "https://forms.gle/o125r2Fv6MdYJV8s9", driveUrl: "https://drive.google.com/drive/folders/1wX_igwL9jm8pJyjRDjc_ObdIs8uTWTcw?usp=sharing",
      formCta: "🗣️ S'inscrire à la Conférence", driveCta: "📁 Règlement & Informations" },
    { id: 6, date: "23 Mai", icon: "🌟", tag: "GALA", tagColor: "#d4a017",
      title: "Unity Day — Grande Soirée Culturelle", subtitle: "Soirée Culturelle",
      hook: "L'événement de l'année : culture et célébration.",
      desc: "Best Poem Challenge, Concours de cuisine, Défilé, Chant & Danse, Festi Bami et Remise de Prix.",
      details: ["Best Poem Challenge · Concours de cuisine camerounaise", "Défilés traditionnels · Chant & Danse", "Festi Bami 2026 · Remise de prix officielle", "23 Mai — L'événement de l'année !"],
      formUrl: "https://forms.gle/BembSfozwPiBcsXq5", driveUrl: "https://drive.google.com/drive/folders/1a_UUVx6YKUkNiW_zaBSLov8yeRXLvRHy?usp=sharing",
      formCta: "🌟 S'inscrire à l'Unity Day", driveCta: "📁 Règlement & Informations" },
  ],
};

// ── Language Context ──────────────────────────────────────────────────────────
const LangContext = createContext<Lang>("it");

// ── Language Switcher ─────────────────────────────────────────────────────────
interface LangSwitcherProps { lang: Lang; setLang: (l: Lang) => void; }

function LangSwitcher({ lang, setLang }: LangSwitcherProps) {
  const langs: { code: Lang; label: string }[] = [
    { code: "it", label: "IT" },
    { code: "en", label: "EN" },
    { code: "fr", label: "FR" },
  ];
  return (
    <div style={{
      position: "fixed", top: "1.2rem", right: "1.2rem", zIndex: 1000,
      display: "flex", gap: "0.3rem",
      background: "rgba(10,10,10,0.85)", border: "1px solid #1a1a1a",
      borderRadius: "6px", padding: "0.3rem 0.4rem", backdropFilter: "blur(8px)",
    }}>
      {langs.map((l) => (
        <button key={l.code} onClick={() => setLang(l.code)} style={{
          background: lang === l.code ? "#d4a017" : "transparent",
          color: lang === l.code ? "#0a0a0a" : "#737373",
          border: "none", padding: "0.25rem 0.55rem", borderRadius: "4px",
          fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em",
          cursor: "pointer", fontFamily: "'Courier New', monospace",
          transition: "background 0.2s, color 0.2s",
        }}>{l.label}</button>
      ))}
    </div>
  );
}



// 1. Tipo aggiunto per la funzione useInView
function useInView(
  threshold = 0.15
): [React.RefObject<HTMLDivElement>, boolean] {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, visible];
}

// 2. Tipi aggiunti per le props di FadeIn
interface FadeInProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}

function FadeIn({ children, delay = 0, className = "" }: FadeInProps) {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

// 3. Tipi aggiunti per le props di HeroSection
interface HeroSectionProps {
  onScroll: () => void;
}

function HeroSection({ onScroll }: HeroSectionProps) {
  const lang = useContext(LangContext);
  const tr = T[lang];
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setTimeout(() => setLoaded(true), 100);
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        padding: "2rem 1.5rem",
        position: "relative",
        overflow: "hidden",
        background:
          "radial-gradient(ellipse at 30% 20%, rgba(16,185,129,0.12) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(212,160,23,0.08) 0%, transparent 50%), #0a0a0a",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "1px",
          height: loaded ? "120px" : "0px",
          background: "linear-gradient(to bottom, transparent, #d4a017)",
          transition: "height 1.2s ease 0.3s",
        }}
      />

      <div
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? "scale(1)" : "scale(0.8)",
          transition: "all 0.8s ease 0.5s",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.75rem",
        }}
      >
        <img
          src="/Logo_Aect.PNG"
          alt="AECT Logo"
          style={{
            width: "90px",
            height: "90px",
            objectFit: "contain",
            borderRadius: "50%",
            border: "2px solid #d4a01733",
            boxShadow: "0 0 20px rgba(212,160,23,0.15)",
          }}
        />
        <div
          style={{
            fontSize: "0.7rem",
            letterSpacing: "0.35em",
            color: "#d4a017",
            fontFamily: "'Courier New', monospace",
            marginBottom: "0.5rem",
            textTransform: "uppercase",
          }}
        >
          {tr.assoc}
        </div>
      </div>

      <h1
        style={{
          fontSize: "clamp(2.2rem, 8vw, 4.5rem)",
          fontWeight: 800,
          fontFamily: "'Georgia', 'Playfair Display', serif",
          background:
            "linear-gradient(135deg, #ffffff 0%, #d4a017 50%, #10b981 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          lineHeight: 1.05,
          margin: "1.5rem 0 0.8rem",
          opacity: loaded ? 1 : 0,
          transform: loaded ? "translateY(0)" : "translateY(40px)",
          transition: "all 1s ease 0.7s",
        }}
      >
        BEYOND BORDERS
        <br />
        PROJECT 2026
        <br />
      </h1>

      <p
        style={{
          fontSize: "clamp(0.95rem, 2.5vw, 1.2rem)",
          color: "#a3a3a3",
          fontFamily: "'Georgia', serif",
          fontStyle: "italic",
          maxWidth: "500px",
          lineHeight: 1.6,
          margin: "0 0 1rem",
          opacity: loaded ? 1 : 0,
          transition: "opacity 1s ease 1s",
        }}
      >
        {tr.tagline}
      </p>

      <p
        style={{
          fontSize: "0.85rem",
          color: "#737373",
          maxWidth: "420px",
          lineHeight: 1.7,
          opacity: loaded ? 1 : 0,
          transition: "opacity 1s ease 1.2s",
        }}
      >
        {tr.subtitle}
      </p>

      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          marginTop: "2rem",
          flexWrap: "wrap",
          justifyContent: "center",
          opacity: loaded ? 1 : 0,
          transition: "opacity 1s ease 1.4s",
        }}
      >
        <button
          onClick={onScroll}
          style={{
            background: "linear-gradient(135deg, #d4a017, #b8860b)",
            color: "#0a0a0a",
            border: "none",
            padding: "0.85rem 2rem",
            borderRadius: "4px",
            fontSize: "0.8rem",
            fontWeight: 700,
            letterSpacing: "0.15em",
            cursor: "pointer",
            textTransform: "uppercase",
            fontFamily: "'Courier New', monospace",
          }}
        >
          {tr.btnBook}
        </button>
      </div>

      <div
        style={{
          position: "absolute",
          bottom: "2rem",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.5rem",
          opacity: loaded ? 0.4 : 0,
          transition: "opacity 1s ease 2s",
          animation: "pulse 2s ease-in-out infinite",
        }}
      >
        <div
          style={{
            fontSize: "0.6rem",
            letterSpacing: "0.2em",
            color: "#737373",
          }}
        >
          {tr.scroll}
        </div>
        <div
          style={{
            width: "1px",
            height: "30px",
            background: "linear-gradient(to bottom, #737373, transparent)",
          }}
        />
      </div>
    </div>
  );
}

function PillarsSection() {
  const lang = useContext(LangContext);
  const tr = T[lang];

  return (
    <div style={{ padding: "4rem 1.5rem", background: "#0a0a0a" }}>
      <FadeIn>
        <div
          style={{
            textAlign: "center",
            marginBottom: "2.5rem",
          }}
        >
          <div
            style={{
              fontSize: "0.65rem",
              letterSpacing: "0.3em",
              color: "#d4a017",
              marginBottom: "0.5rem",
            }}
          >
            {tr.pillarsLabel}
          </div>
          <h2
            style={{
              fontSize: "clamp(1.5rem, 5vw, 2.2rem)",
              color: "#fafafa",
              fontFamily: "'Georgia', serif",
              fontWeight: 700,
            }}
          >
            {tr.pillarsTitle}
          </h2>
        </div>
      </FadeIn>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.5rem",
          maxWidth: "900px",
          margin: "0 auto",
        }}
      >
        {tr.pillars.map((p, i) => (
          <FadeIn key={i} delay={i * 0.15}>
            <div
              style={{
                background: "#111111",
                border: "1px solid #1a1a1a",
                borderRadius: "8px",
                padding: "2rem 1.5rem",
                textAlign: "center",
                transition: "border-color 0.3s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.borderColor = "#d4a01744")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.borderColor = "#1a1a1a")
              }
            >
              <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>
                {p.icon}
              </div>
              <h3
                style={{
                  fontSize: "1.1rem",
                  color: "#fafafa",
                  fontFamily: "'Georgia', serif",
                  marginBottom: "0.5rem",
                }}
              >
                {p.title}
              </h3>
              <p
                style={{
                  fontSize: "0.82rem",
                  color: "#737373",
                  lineHeight: 1.6,
                }}
              >
                {p.text}
              </p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  );
}

function TimelineSection() {
  const lang = useContext(LangContext);
  const tr = T[lang];
  const events = EVENTS_MULTI[lang];
  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <div
      id="timeline"
      style={{
        padding: "4rem 1.5rem",
        background: "linear-gradient(180deg, #0a0a0a 0%, #0d1117 100%)",
      }}
    >
      <FadeIn>
        <div style={{ textAlign: "center", marginBottom: "3rem" }}>
          <div
            style={{
              fontSize: "0.65rem",
              letterSpacing: "0.3em",
              color: "#10b981",
              marginBottom: "0.5rem",
            }}
          >
            {tr.calLabel}
          </div>
          <h2
            style={{
              fontSize: "clamp(1.5rem, 5vw, 2.2rem)",
              color: "#fafafa",
              fontFamily: "'Georgia', serif",
              fontWeight: 700,
            }}
          >
            {tr.calTitle}
          </h2>
          <p
            style={{
              fontSize: "0.82rem",
              color: "#525252",
              marginTop: "0.5rem",
            }}
          >
            {tr.calSub}
          </p>
        </div>
      </FadeIn>

      <div
        style={{ maxWidth: "680px", margin: "0 auto", position: "relative" }}
      >
        <div
          style={{
            position: "absolute",
            left: "20px",
            top: 0,
            bottom: 0,
            width: "1px",
            background:
              "linear-gradient(to bottom, #d4a01733, #10b98133, transparent)",
          }}
        />

        {events.map((ev, i) => (
          <FadeIn key={ev.id} delay={i * 0.1}>
            <div
              style={{
                display: "flex",
                gap: "1.5rem",
                marginBottom: "2rem",
                paddingLeft: "0",
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: "40px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  paddingTop: "0.3rem",
                }}
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    background: ev.tagColor,
                    border: `2px solid ${ev.tagColor}33`,
                    boxShadow: `0 0 12px ${ev.tagColor}44`,
                  }}
                />
              </div>

              <div
                style={{
                  flex: 1,
                  background: "#111111",
                  border: "1px solid #1a1a1a",
                  borderRadius: "8px",
                  padding: "1.5rem",
                  transition: "border-color 0.3s, transform 0.3s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = ev.tagColor + "55";
                  e.currentTarget.style.transform = "translateX(4px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#1a1a1a";
                  e.currentTarget.style.transform = "translateX(0)";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "0.5rem",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.65rem",
                      letterSpacing: "0.15em",
                      color: ev.tagColor,
                      fontFamily: "'Courier New', monospace",
                      fontWeight: 700,
                    }}
                  >
                    {ev.date}
                  </div>
                  <span
                    style={{
                      fontSize: "0.55rem",
                      background: ev.tagColor + "22",
                      color: ev.tagColor,
                      padding: "2px 8px",
                      borderRadius: "3px",
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                    }}
                  >
                    {ev.tag}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "0.6rem",
                    alignItems: "center",
                    marginBottom: "0.4rem",
                  }}
                >
                  <span style={{ fontSize: "1.3rem" }}>{ev.icon}</span>
                  <h3
                    style={{
                      fontSize: "1.1rem",
                      color: "#fafafa",
                      fontFamily: "'Georgia', serif",
                      fontWeight: 700,
                    }}
                  >
                    {ev.title}
                  </h3>
                </div>

                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#d4a017",
                    fontStyle: "italic",
                    fontFamily: "'Georgia', serif",
                    marginBottom: "0.5rem",
                    lineHeight: 1.4,
                  }}
                >
                  "{ev.hook}"
                </p>

                <p
                  style={{
                    fontSize: "0.78rem",
                    color: "#737373",
                    lineHeight: 1.6,
                  }}
                >
                  {ev.desc}
                </p>

                {ev.details && ev.details.length > 0 && (
                  <div style={{ marginTop: "0.65rem", borderTop: "1px solid #1f1f1f", paddingTop: "0.4rem" }}>
                    <button
                      onClick={() => setExpandedId(expandedId === ev.id ? null : ev.id)}
                      style={{
                        background: "transparent", border: "none",
                        color: expandedId === ev.id ? "#525252" : ev.tagColor,
                        cursor: "pointer", fontSize: "0.62rem", fontWeight: 700,
                        padding: "0.25rem 0", letterSpacing: "0.12em",
                        fontFamily: "'Courier New', monospace",
                        display: "flex", alignItems: "center", gap: "0.35rem",
                        transition: "color 0.2s ease",
                      }}
                    >
                      <span style={{
                        display: "inline-block",
                        transform: expandedId === ev.id ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.3s ease",
                        fontSize: "0.7rem",
                      }}>▾</span>
                      {expandedId === ev.id ? tr.lessBtn : tr.moreBtn}
                    </button>

                    {expandedId === ev.id && (
                      <ul style={{ listStyle: "none", margin: "0.4rem 0 0.2rem", padding: 0 }}>
                        {ev.details.map((d, idx) => (
                          <li key={idx} style={{
                            fontSize: "0.73rem", color: "#a3a3a3", lineHeight: 1.65,
                            padding: "0.15rem 0",
                            display: "flex", gap: "0.4rem", alignItems: "flex-start",
                          }}>
                            <span style={{ color: ev.tagColor, flexShrink: 0, marginTop: "0.1rem" }}>›</span>
                            {d}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {(ev.formUrl || ev.driveUrl) && (
                  <div style={{ marginTop: "1rem", display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
                    {ev.formUrl && (
                      <a
                        href={ev.formUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex", alignItems: "center",
                          background: "linear-gradient(135deg, #d4a017, #b8860b)",
                          color: "#0a0a0a",
                          padding: "0.55rem 1rem",
                          borderRadius: "4px",
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          letterSpacing: "0.07em",
                          textDecoration: "none",
                          fontFamily: "'Courier New', monospace",
                          transition: "transform 0.2s, box-shadow 0.2s",
                          boxShadow: "0 4px 12px rgba(212,160,23,0.2)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = "0 8px 20px rgba(212,160,23,0.35)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(212,160,23,0.2)";
                        }}
                      >
                        {ev.formCta}
                      </a>
                    )}
                    {ev.driveUrl && (
                      <a
                        href={ev.driveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex", alignItems: "center",
                          background: "transparent",
                          color: ev.tagColor,
                          border: `1px solid ${ev.tagColor}55`,
                          padding: "0.55rem 1rem",
                          borderRadius: "4px",
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          letterSpacing: "0.07em",
                          textDecoration: "none",
                          fontFamily: "'Courier New', monospace",
                          transition: "background 0.2s, border-color 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = ev.tagColor + "15";
                          e.currentTarget.style.borderColor = ev.tagColor + "aa";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.borderColor = ev.tagColor + "55";
                        }}
                      >
                        {ev.driveCta}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  );
}

function FormSection() {
  const lang = useContext(LangContext);
  const tr = T[lang];

  return (
    <div
      id="form-section"
      style={{
        padding: "6rem 1.5rem",
        background: "linear-gradient(180deg, #0d1117 0%, #0a0a0a 100%)",
        textAlign: "center",
      }}
    >
      <FadeIn>
        <div style={{ maxWidth: "600px", margin: "0 auto" }}>
          <div
            style={{
              fontSize: "0.7rem",
              letterSpacing: "0.3em",
              color: "#d4a017",
              marginBottom: "1rem",
              fontWeight: "bold",
            }}
          >
            {tr.regLabel}
          </div>

          <h2
            style={{
              fontSize: "clamp(2rem, 5vw, 2.5rem)",
              color: "#fafafa",
              fontFamily: "'Georgia', serif",
              fontWeight: 700,
              marginBottom: "1.5rem",
            }}
          >
            {tr.regTitle}
          </h2>

          <p
            style={{
              color: "#a3a3a3",
              fontSize: "1.05rem",
              lineHeight: 1.6,
              marginBottom: "3rem",
            }}
          >
            {tr.regDesc}
            {" "}<br />
            {tr.regDesc2}
          </p>

          {/* 4. Corretto e.target in e.currentTarget */}
          <button
            onClick={() =>
              document.getElementById("timeline")?.scrollIntoView({ behavior: "smooth" })
            }
            style={{
              background: "linear-gradient(135deg, #d4a017, #b8860b)",
              color: "#0a0a0a",
              border: "none",
              padding: "1.2rem 3rem",
              borderRadius: "50px",
              fontWeight: 800,
              cursor: "pointer",
              fontSize: "1rem",
              textTransform: "uppercase",
              letterSpacing: "0.15em",
              fontFamily: "'Courier New', monospace",
              boxShadow: "0 10px 25px rgba(212, 160, 23, 0.15)",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow =
                "0 15px 35px rgba(212, 160, 23, 0.3)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 10px 25px rgba(212, 160, 23, 0.15)";
            }}
          >
            {tr.regBtn}
          </button>
        </div>
      </FadeIn>
    </div>
  );
}

function PartnersSection() {
  const lang = useContext(LangContext);
  const tr = T[lang];

  return (
    <div
      style={{
        padding: "3.5rem 1.5rem",
        background: "#ffffff",
        borderTop: "1px solid #ddd5c8",
        borderBottom: "1px solid #ddd5c8",
        textAlign: "center",
      }}
    >
      <FadeIn>
        <div
          style={{
            fontSize: "0.6rem",
            letterSpacing: "0.35em",
            color: "#8b6914",
            marginBottom: "2rem",
            fontFamily: "'Courier New', monospace",
            textTransform: "uppercase",
          }}
        >
          {tr.partnersLabel}
        </div>
        <img
          src="/LOGO_PARTENAIRES.png"
          alt="Partners"
          style={{
            maxWidth: "min(820px, 90vw)",
            width: "100%",
            opacity: 1,
            transition: "opacity 0.3s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        />
      </FadeIn>
    </div>
  );
}

function FooterSection() {
  const lang = useContext(LangContext);
  const tr = T[lang];

  return (
    <div
      style={{
        padding: "3rem 1.5rem 2rem",
        background: "#0a0a0a",
        borderTop: "1px solid #1a1a1a",
        textAlign: "center",
      }}
    >
      <FadeIn>
        <div style={{ maxWidth: "500px", margin: "0 auto" }}>
          <button
            onClick={() =>
              document.getElementById("timeline")?.scrollIntoView({ behavior: "smooth" })
            }
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "fit-content",
              margin: "0 auto 1rem",
              padding: "0.75rem 1.5rem",
              background: "linear-gradient(135deg, #d4a017, #b8860b)",
              color: "#0a0a0a",
              borderRadius: "4px",
              fontSize: "0.75rem",
              fontWeight: 800,
              letterSpacing: "0.15em",
              textDecoration: "none",
              textTransform: "uppercase",
              fontFamily: "'Courier New', monospace",
              border: "none",
              cursor: "pointer",
            }}
          >
            {tr.footerBtn}
          </button>

          <p
            style={{
              fontSize: "0.82rem",
              color: "#737373",
              fontStyle: "italic",
              fontFamily: "'Georgia', serif",
              marginBottom: "0.5rem",
            }}
          >
            {tr.footerQuote}
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "1.5rem",
              marginTop: "1.5rem",
              marginBottom: "1.5rem",
            }}
          >
            {[
              { label: tr.facebook, href: "https://www.facebook.com/aect.cameroun" },
              { label: tr.instagram, href: "https://www.instagram.com/aect_turin/" },
              { label: tr.website, href: "https://www.aect-turin.com" },
            ].map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: "0.7rem",
                  color: "#525252",
                  textDecoration: "none",
                  letterSpacing: "0.1em",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#d4a017")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#525252")}
              >
                {s.label}
              </a>
            ))}
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            <img
              src="/LOGO_PARTENAIRES.png"
              alt="Partners"
              style={{
                maxWidth: "320px",
                width: "100%",
                opacity: 0.45,
                filter: "grayscale(30%)",
                transition: "opacity 0.3s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.75")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.45")}
            />
          </div>

          <div style={{ borderTop: "1px solid #1a1a1a", paddingTop: "1.5rem" }}>
            <p
              style={{ fontSize: "0.7rem", color: "#404040", lineHeight: 1.6 }}
            >
              Associazione degli Studenti Camerunensi di Torino — AECT
              <br />
              Corso Duca degli Abruzzi 24, 10129 Torino
              <br />
              bureauaect@gmail.com · +39 379 122 8086
            </p>
            <p
              style={{
                fontSize: "0.6rem",
                color: "#262626",
                marginTop: "0.75rem",
              }}
            >
              www.aect-turin.com · @Aect Turin
            </p>
          </div>
        </div>
      </FadeIn>
    </div>
  );
}

export default function BeyondBorders2026() {
  const [lang, setLang] = useState<Lang>("it");
  const scrollToTimeline = () => {
    document.getElementById("timeline")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <LangContext.Provider value={lang}>
      <div
        style={{
          fontFamily: "'Segoe UI', -apple-system, sans-serif",
          background: "#0a0a0a",
          color: "#fafafa",
          minHeight: "100vh",
          overflowX: "hidden",
        }}
      >
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 0.4; }
            50% { opacity: 0.8; }
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          ::selection { background: #d4a01744; color: #fafafa; }
          @media (max-width: 480px) {
            h1 { font-size: 2.5rem !important; }
          }
        `}</style>

        <LangSwitcher lang={lang} setLang={setLang} />
        <HeroSection onScroll={scrollToTimeline} />
        <PillarsSection />
        <TimelineSection />
        <FormSection />
        <PartnersSection />
        <FooterSection />
      </div>
    </LangContext.Provider>
  );
}
